import { API_URL } from '../config';

export interface ThemeSettings {
  fontSize: number;
  isDarkMode: boolean;
  colorScheme: string;
}

// CDRPL Color Schemes
export const COLOR_SCHEMES = {
  GREEN: '#1d8348',
  ORANGE: '#e67e22',
  BLUE: '#1f4aba'
} as const;

export const COLOR_SCHEME_OPTIONS = [
  { label: 'Green (Default)', value: COLOR_SCHEMES.GREEN },
  { label: 'Orange', value: COLOR_SCHEMES.ORANGE },
  { label: 'Blue', value: COLOR_SCHEMES.BLUE }
];

// Fixed primary color for the entire application
export const PRIMARY_COLOR = '#1d8348 ';

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  fontSize: 14,
  isDarkMode: false,
  colorScheme: COLOR_SCHEMES.GREEN
};

export const getThemeSettings = async (): Promise<ThemeSettings | null> => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    const response = await fetch(`${API_URL}/core/preferences`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        return DEFAULT_THEME_SETTINGS;
      }
      throw new Error('Failed to load preferences');
    }
    
    const data = await response.json();
    
    if (data?.theme_config) {
      const { fontSize, isDarkMode, colorScheme } = data.theme_config;
      return { 
        fontSize, 
        isDarkMode, 
        colorScheme: colorScheme || COLOR_SCHEMES.GREEN 
      };
    }
    
    return DEFAULT_THEME_SETTINGS;
  } catch (error) {
    console.error('Failed to load theme settings:', error);
    return DEFAULT_THEME_SETTINGS;
  }
};

export const saveThemeSettings = async (settings: ThemeSettings): Promise<boolean> => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;

    const response = await fetch(`${API_URL}/core/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        theme_config: settings
      })
    });

    return response.ok;
  } catch (error) {
    console.error('Failed to save theme settings:', error);
    return false;
  }
};

/**
 * Get theme settings from local storage
 */
export const getStoredThemeSettings = (): ThemeSettings | null => {
  try {
    const stored = localStorage.getItem('themeSettings');
    if (stored) {
      const parsed = JSON.parse(stored) as ThemeSettings;
      if (typeof parsed.fontSize === 'number' && 
          typeof parsed.isDarkMode === 'boolean') {
        return {
          fontSize: parsed.fontSize,
          isDarkMode: parsed.isDarkMode,
          colorScheme: parsed.colorScheme || COLOR_SCHEMES.GREEN
        };
      }
    }
    return null;
  } catch (e) {
    console.error('Error parsing stored theme settings:', e);
    return null;
  }
};

export const saveStoredThemeSettings = (settings: ThemeSettings): void => {
  localStorage.setItem('themeSettings', JSON.stringify(settings));
}; 